/**
 * Voice mode as a state machine: what it is doing, and what it must do next.
 * Pure, so the rules about turns and interruptions can be tested without a
 * microphone; `useVoice` carries out the effects.
 *
 *   listening ──end of your turn──▶ thinking ──first words──▶ speaking
 *       ▲                              │                        │
 *       └──────── you talk over it ────┴────── reply said ──────┘
 *
 * Talking over the reply stops it. What the microphone hears of the voice
 * itself (`echo`) is not talking over it.
 */

export type VoicePhase = 'connecting' | 'listening' | 'thinking' | 'speaking' | 'ended';

export interface VoiceState {
  phase: VoicePhase;
  /** What you are saying, as it is heard. */
  you: string;
  /** What the reply has said so far. */
  reply: string;
  /** The reply's stream has finished; speech may still be playing. */
  turnDone: boolean;
  muted: boolean;
  error: string | null;
}

export type VoiceEvent =
  | { type: 'opened' }
  | { type: 'heard'; phase: 'start' | 'update' | 'end' | 'resumed'; text: string; echo: boolean }
  | { type: 'reply'; text: string }
  | { type: 'saying' }
  | { type: 'turnDone' }
  | { type: 'speakerIdle' }
  | { type: 'turnFailed'; message: string }
  | { type: 'mute'; muted: boolean }
  | { type: 'failed'; message: string }
  | { type: 'end' };

export type VoiceEffect = { type: 'send'; text: string } | { type: 'interrupt' };

export const VOICE_START: VoiceState = {
  phase: 'connecting',
  you: '',
  reply: '',
  turnDone: false,
  muted: false,
  error: null
};

const listening = (s: VoiceState, you = ''): VoiceState => ({ ...s, phase: 'listening', you, reply: '', turnDone: false });

export function voiceStep(s: VoiceState, e: VoiceEvent): [VoiceState, VoiceEffect[]] {
  if (s.phase === 'ended') return [s, []];
  switch (e.type) {
    case 'opened':
      return [s.phase === 'connecting' ? listening(s) : s, []];
    case 'mute':
      return [{ ...s, muted: e.muted }, []];
    case 'failed':
      return [{ ...s, phase: 'ended', error: e.message }, [{ type: 'interrupt' }]];
    case 'end':
      return [{ ...s, phase: 'ended' }, s.phase === 'thinking' || s.phase === 'speaking' ? [{ type: 'interrupt' }] : []];
    case 'heard': {
      const text = e.text.trim();
      if (s.phase === 'listening') {
        if (e.phase === 'end') return text ? [{ ...s, phase: 'thinking', you: text, reply: '', turnDone: false }, [{ type: 'send', text }]] : [s, []];
        return [{ ...s, you: text || s.you, error: null }, []];
      }
      if ((s.phase === 'thinking' || s.phase === 'speaking') && text && !e.echo) {
        // Talked over: stop the reply, and listen to this instead.
        if (e.phase === 'end') return [{ ...listening(s), phase: 'thinking', you: text }, [{ type: 'interrupt' }, { type: 'send', text }]];
        return [listening(s, text), [{ type: 'interrupt' }]];
      }
      return [s, []];
    }
    case 'reply':
      return s.phase === 'thinking' || s.phase === 'speaking' ? [{ ...s, reply: s.reply + e.text }, []] : [s, []];
    case 'saying':
      return [s.phase === 'thinking' ? { ...s, phase: 'speaking' } : s, []];
    case 'turnDone':
      if (s.phase === 'thinking' && !s.reply.trim()) return [listening(s), []];
      return [s.phase === 'thinking' || s.phase === 'speaking' ? { ...s, turnDone: true } : s, []];
    case 'speakerIdle':
      return [s.phase === 'speaking' && s.turnDone ? listening(s) : s, []];
    case 'turnFailed':
      return [s.phase === 'thinking' || s.phase === 'speaking' ? { ...listening(s), error: e.message } : s, []];
  }
}
